import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import { ChangeDetectionStrategy, Component, EventEmitter, effect, inject, input, signal } from "@angular/core";

import { WebsocketService } from "../../../helpers/websocket.service";
import { WebsocketListener } from "../../../helpers/websocketListener";
import { SelfserviceStatusMessage } from "../../dialogs/dialog-selfservice/dialog-selfservice";
import { SelfserviceAuthenticationBox } from "../selfservice-authentication-box/selfservice-authentication-box";

export interface SubscriptionUpdateMessage<S> {
  kind: "subscription-update";
  subscription: S;
}
export interface ProcessProgressMessage {
  kind: "process-progress";
  processName: string;
  percentage: number;
  status: string;
}
export type LoaderMessage<S> = ProcessProgressMessage | SubscriptionUpdateMessage<S>;

const phraseSelfserviceWsUrl = (processId: string) => `fulfilment/process/${processId}`;

@Component({
  selector: "selfservice-loader",
  templateUrl: "selfservice-loader.html",
  styleUrl: "selfservice-loader.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, SelfserviceAuthenticationBox, TranslatePipe],
})
export class SelfserviceLoader<S> {
  private wsService = inject(WebsocketService);

  processId = input.required<string>();
  bus = input.required<EventEmitter<SelfserviceStatusMessage>>();
  protected progress = signal(0);
  protected errorState = signal(false);
  protected loading = signal(true);

  private timerId: NodeJS.Timeout | undefined;
  private websocketListener: WebsocketListener<LoaderMessage<S>>;

  constructor() {
    this.websocketListener = new WebsocketListener(this.wsService);

    effect(() => {
      const processId = this.processId();
      const websocketUrl = phraseSelfserviceWsUrl(processId);
      const handleMessage = (msg: LoaderMessage<S>) => {
        switch (msg.kind) {
          case "process-progress":
            this.progress.set(msg.percentage);
            const isFailed = msg.status === "failed";
            const isCompleted = msg.status === "completed";
            if (isCompleted || isFailed) {
              this.errorState.set(isFailed);
              clearInterval(this.timerId);
              this.websocketListener.close();
              const status = isCompleted ? "completed" : "error";
              this.bus().emit(status);
            }
            break;
          default:
            console.warn("Unexpected state: Should not be received");
            break;
        }
      };

      const handleError = () => {
        this.errorState.set(true);
      };

      this.websocketListener.listen(websocketUrl, { next: handleMessage, error: handleError });
      this.timerId = setTimeout(() => this.errorState.set(true), 600000);
    });
  }

  protected close() {
    this.bus().emit("error");
    this.websocketListener.close();
  }

  protected notifySupport() {
    this.bus().emit("notify_support");
  }
}
